import { useEffect, useMemo, useState } from 'react';
import { cachedGet } from '../../utils/lookupCache';
import type { TablemaintField } from '../../config/dataTables.registry';
import type { Option } from './fields';

/** Option lists fetched from `optionsEndpoint`, keyed by endpoint. An endpoint returns
 *  either string[] or the full Option shape — the FK lookups (/cms-lookups/billtos,
 *  /locations, /customers) carry `labelSuffix` (the '#recno') and `description`, so the
 *  map must be typed wide enough to keep them rather than narrowing to {value,label}. */
export type FieldOptionsMap = Record<string, (string | Option)[]>;

/**
 * Fetch every distinct `optionsEndpoint` referenced by `fields`, once.
 *
 * Keyed on the resolved endpoint list rather than the `fields` array identity, so
 * a caller passing an inline `cfg.fields ?? []` doesn't re-run the effect on every
 * render. A failed endpoint resolves to [] so one bad lookup can't blank the rest.
 *
 * Goes through the shared lookupCache: these lists barely change, and the same
 * endpoints are hit by many grids and by both the page and a standalone drawer.
 */
export function useFieldOptions(fields: TablemaintField[]): FieldOptionsMap {
  const endpoints = useMemo(
    () => Array.from(new Set(fields.map((f) => f.optionsEndpoint).filter((e): e is string => !!e))),
    [fields],
  );
  const endpointsKey = endpoints.join('|');
  const [options, setOptions] = useState<FieldOptionsMap>({});

  useEffect(() => {
    if (endpoints.length === 0) return;
    const ctrl = new AbortController();
    Promise.all(
      endpoints.map((e) =>
        // Reference data shared across ~12 maintenance grids — served from the shared
        // 5-min lookupCache rather than refetched per mount (John's 0a1f2ba53 sweep,
        // which landed on this fetch while it still lived inside TablemaintPage).
        cachedGet<(string | Option)[]>(e, { signal: ctrl.signal })
          .then((res) => [e, res.data ?? []] as const)
          .catch(() => [e, [] as (string | Option)[]] as const)),
    ).then((pairs) => setOptions(Object.fromEntries(pairs)));
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointsKey]);

  return options;
}

export default useFieldOptions;
